import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import Joi from 'joi';
import FormController from 'common/formController';
import {
  createList,
  createSegmentedList,
  getPaginatedLists,
  updateSegmentedList
} from 'store/lists/actions';
import validators from './validators';

let formController = new FormController();

export default function useLists() {
  const dispatch = useDispatch();
  const { listId, segmentedListId } = useParams();

  const currentSegmentedList = useSelector(
    store => store.lists.segmentedList.currentList
  );
  const isSaveListNameSucceeded = useSelector(
    store => store.lists.subscribersList.savedName
  );
  const isDeleteListSucceeded = useSelector(
    store => store.lists.subscribersList.deleted
  );

  const [thisList, setThisList] = useState({ listName: '' });
  const [thisSegmentedList, setThisSegmentedList] = useState({ name: '' });
  const [openCreateListModal, setOpenCreateListModal] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [errorValidationSegment, setErrorValidationSegment] = useState(null);

  useEffect(() => {
    formController = new FormController();
    setThisSegmentedList(currentSegmentedList);
  }, [currentSegmentedList]);

  const onClickAddList = () => {
    setOpenCreateListModal(true);
  };

  useEffect(() => {
    if (isSaveListNameSucceeded || isDeleteListSucceeded) {
      dispatch(getPaginatedLists({ force: true }));
    }
  }, [isSaveListNameSucceeded, isDeleteListSucceeded]);

  const validate = (saveAttempted = false, list) => {
    let segment = errorValidationSegment;
    const joiValidators = Joi.object(validators[segment]);
    const validation = joiValidators.validate(
      list ||
        (segment === 'createSegmentedList' ? thisSegmentedList : thisList),
      {
        abortEarly: false,
        allowUnknown: true // allow unknown keys that will be ignored
      }
    );
    let errors = formController.getFormErrors(validation, { saveAttempted });
    setValidationErrors(errors);
    return errors;
  };

  const setListName = value => {
    let updatedList = { ...thisList, listName: value };
    setThisList(updatedList);
    validate(false, updatedList);
  };

  const setSegmentedListName = value => {
    let updatedList = { ...thisSegmentedList, name: value };
    setThisSegmentedList(updatedList);
    validate(false, updatedList);
  };

  const onBlur = event => {
    formController.onBlur(event);
    validate();
  };

  const onClickSaveListName = () => {
    let errors = validate(true);
    if (errors.meta.clean) {
      dispatch(createList(thisList.listName));
      closeListModal();
    }
  };

  const onClickSaveSegmentedList = () => {
    let errors = validate(true);
    if (errors.meta.clean) {
      if (segmentedListId) {
        dispatch(
          updateSegmentedList({
            segmentedListId: segmentedListId,
            segmentedList: thisSegmentedList
          })
        );
      } else {
        dispatch(
          createSegmentedList({
            listId: listId,
            segmentedList: thisSegmentedList
          })
        );
      }
    }
  };

  const updateCurrentErrorValidationSegment = name => {
    resetValidationErrors();
    setErrorValidationSegment(name);
  };

  const shouldShowError = name => {
    return formController.shouldShowError(validationErrors, name);
  };

  const getErrorMessage = name => {
    return formController.getErrorMessage(validationErrors, name);
  };

  const closeListModal = () => {
    setOpenCreateListModal(false);
    formController = new FormController();
    setThisList({ ...thisList, listName: '' });
    setValidationErrors({});
  };

  const handleChange = event => {
    let updatedList = { ...thisSegmentedList, segmentDefinition: event };
    setThisSegmentedList(updatedList);
    validate(false, updatedList);
  };

  const validateForErrors = (name, isSaveAttempted) => {
    return validate(isSaveAttempted || false, { listName: name });
  };

  const resetValidationErrors = () => {
    formController = new FormController();
    setValidationErrors({});
    setThisSegmentedList({
      name: ''
    });
  };

  return {
    thisList,
    thisSegmentedList,
    openCreateListModal,
    validationErrors,
    onBlur,
    validate,
    setListName,
    onClickSaveListName,
    updateCurrentErrorValidationSegment,
    shouldShowError,
    getErrorMessage,
    closeListModal,
    onClickAddList,
    setSegmentedListName,
    onClickSaveSegmentedList,
    handleChange,
    resetValidationErrors,
    validateForErrors
  };
}
